import { c as _c } from "react/compiler-runtime";
import { postHostedIdentifySessionChallenge, postHostedIdentifySessionChallengeVerify } from '@onefootprint/fetch-hosted';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getBiometricChallengeResponse, getLogger } from '@/idv/utils';
const {
  logWarn
} = getLogger({
  location: 'run-passkey'
});
type UseRunPasskeyProps = {
  onDone?: (delayMs: number) => Promise<void>;
};
export const runPasskeyChallenge = async (identifyToken: string) => {
  const headers = {
    'X-Fp-Authorization': identifyToken
  };
  const {
    data: {
      challengeData,
      error
    }
  } = await postHostedIdentifySessionChallenge({
    headers,
    body: {
      challengeKind: 'biometric'
    }
  });
  if (error) {
    throw error;
  }
  const {
    biometricChallengeJson,
    challengeKind
  } = challengeData;
  if (challengeKind !== 'biometric' || !biometricChallengeJson) {
    logWarn('Received incorrect challenge after requesting login biometric challenge');
    return;
  }
  let challengeResponse: string;
  try {
    challengeResponse = await getBiometricChallengeResponse(biometricChallengeJson);
  } catch (error) {
    throw {
      stage: 'get-challenge-response',
      error
    };
  }
  await postHostedIdentifySessionChallengeVerify({
    headers,
    body: {
      challengeResponse
    }
  });
};
const useRunPasskey = t0 => {
  const $ = _c(10);
  const {
    onDone
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "passkey-challenge"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("identify", t1);
  const showRequestErrorToast = useRequestErrorToast();
  const {
    toast
  } = useToast();
  let t2;
  if ($[1] !== onDone) {
    t2 = async identifyToken => {
      await runPasskeyChallenge(identifyToken);
      await onDone?.(0);
    };
    $[1] = onDone;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== showRequestErrorToast || $[4] !== t || $[5] !== toast) {
    t3 = error => {
      if (isGetChallengeResponseError(error)) {
        const e = error.error;
        logWarn(`Unable to generate biometric challenge response: ${e instanceof Error ? e.message : String(e)}`, e);
        toast({
          title: t(_temp),
          description: t(_temp2),
          variant: "error"
        });
      } else {
        logWarn("Error while running passkey challenge:", error);
        showRequestErrorToast(error);
      }
    };
    $[3] = showRequestErrorToast;
    $[4] = t;
    $[5] = toast;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = {
      mutationFn: t2,
      onError: t3
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useMutation(t4);
};
type StagedError = {
  stage: string;
  error: unknown;
};
export const isGetChallengeResponseError = (error: any): error is StagedError => {
  return typeof error === 'object' && 'stage' in error && error.stage === 'get-challenge-response';
};
export default useRunPasskey;
function _temp($) {
  return $.error.title;
}
function _temp2($_0) {
  return $_0.error.description;
}